import React from "react";
import { Link } from "react-router-dom";

import { Box, Typography } from "@material-ui/core";

import { ArrayField, Datagrid, FunctionField, TextField } from "react-admin";

import { CopyToClipboardButton } from "../../../components/common/CopyToClipboardButton";
import { AclType, CarrierUserTypeChoices } from "../../../utils/constants";
import { isAuthorized } from "../../../utils/appUtils";
import { formatPhoneNumber, renderFullName } from "../../../utils/utils";

// The junction row's `type` is the storage form (CARRIER_DISPATCH etc.); the
// same choices the user form edits it with supply the label. An unbackfilled
// legacy or short-form value has no match — show it raw rather than blank, so
// the row still says something rather than reading as a user with no role.
const renderRole = (record) =>
  CarrierUserTypeChoices.find((choice) => choice.id === record.type)?.name ||
  record.type ||
  "";

const renderPhone = (record) => formatPhoneNumber(record.phone);

// A person may hold more than one junction row on the same carrier (one per
// yard). The tab lists people, so the first row for each wins.
const dedupeByUser = (users) =>
  users.reduce((acc, user) => {
    if (!acc.find((u) => u.carrierUserId === user.carrierUserId)) {
      acc.push(user);
    }
    return acc;
  }, []);

const NameField = ({ record, linkBackPath }) => {
  const fullName = renderFullName(record);

  if (!isAuthorized(AclType.USERS)) {
    return <Typography variant="body2">{fullName}</Typography>;
  }

  return (
    <Link
      to={{
        pathname: `/carrierUsers/${record.carrierUserId}`,
        state: { backPath: linkBackPath },
      }}
    >
      <Typography variant="body2" color="primary">
        {fullName}
      </Typography>
    </Link>
  );
};

const EmailField = ({ record }) => (
  <Box display="flex" alignItems="center">
    <TextField source="email" record={record} />
    {record.email && <CopyToClipboardButton text={record.email} mb={0} />}
  </Box>
);

export const CarrierUsersTab = ({ record, backPath, location, ...props }) => {
  const users = dedupeByUser(record?.users?.items || []);
  const linkBackPath = backPath
    ? [...backPath, location?.pathname]
    : [location?.pathname];

  if (users.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No active users
      </Typography>
    );
  }

  return (
    // The rows are already in hand on the carrier record, so this is a plain
    // ArrayField over a synthetic record rather than a second fetch.
    <ArrayField source="items" record={{ items: users }} fullWidth>
      <Datagrid>
        <NameField label="Name" linkBackPath={linkBackPath} />
        <FunctionField label="Role" render={renderRole} />
        <FunctionField label="Phone" render={renderPhone} />
        <EmailField label="Email" />
      </Datagrid>
    </ArrayField>
  );
};
